import {useEffect, useState} from "react";

const useResizeObserver = (ref, debounceDelay = 200, options = {}) => {
  const {getInitialWidth = false} = options;

  const [dimensions, setDimensions] = useState(() => ({
    width:
      getInitialWidth && ref.current
        ? ref.current.getBoundingClientRect().width
        : 0,
    height:
      getInitialWidth && ref.current
        ? ref.current.getBoundingClientRect().height
        : 0,
  }));

  useEffect(() => {
    let timeoutId;
    let resizeObserver;
    let mutationObserver;

    const setupResizeObserver = (element) => {
      if (!element) return;

      if (getInitialWidth) {
        const rect = element.getBoundingClientRect();
        setDimensions({width: rect.width, height: rect.height});
      }

      resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            const {width, height} = entry.contentRect;
            setDimensions({width, height});
          }, debounceDelay);
        }
      });

      resizeObserver.observe(element);
    };

    if (ref.current) {
      setupResizeObserver(ref.current);
    }

    if (getInitialWidth && !ref.current) {
      mutationObserver = new MutationObserver((mutations) => {
        if (ref.current) {
          setupResizeObserver(ref.current);
          mutationObserver.disconnect();
        }
      });

      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      clearTimeout(timeoutId);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
    };
  }, [ref, debounceDelay, getInitialWidth]);

  return dimensions;
};

export default useResizeObserver;
